import { useState } from "react";
import { Hammer, Swords, Shield, Dices, Check, RotateCw, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Label, inputCls } from "./shared";
import { HelmetGlyph } from "@/components/HelmetGlyph";

const CLASSES = ["Dimachaerus", "Retiarius", "Provocator"];
const GRADES = ["Poor", "Average", "Good", "Excellent", "Elite"];
const ROLL_RARITIES = ["uncommon", "rare", "elite", "legendary"];
const RARITY_TEXT = { uncommon: "text-emerald-300", rare: "text-sky-300", elite: "text-fuchsia-300", legendary: "text-amber-300" };
const SKILL_LABELS = { strength: "Strength", speed: "Speed", defense: "Defense", stamina: "Stamina", technique: "Technique", precision: "Precision" };

const HelmetSlotIcon = ({ size = 13, className = "" }) => <HelmetGlyph size={size} className={className} />;

export function WorkshopPanel() {
  // ---- Create gladiator ----
  const [name, setName] = useState("");
  const [cls, setCls] = useState("Provocator");
  const [skillValue, setSkillValue] = useState(40);
  const [grade, setGrade] = useState("Good");
  const [gBusy, setGBusy] = useState(false);

  const createGlad = async () => {
    const sv = Number(skillValue);
    if (!sv || sv < 1) return toast.error("Enter a skill value of at least 1");
    setGBusy(true);
    try {
      const { data } = await api.post("/admin/gladiators/create", {
        name: name.trim() || null, cls, skill_value: sv, potential_grade: grade,
      });
      toast.success(`Created ${data.gladiator.name} (${cls}, ${grade}). Find them on your roster to list for auction.`);
      setName("");
    } catch (e) { toast.error(apiErr(e)); } finally { setGBusy(false); }
  };

  // ---- Roll gear ----
  const [kind, setKind] = useState("weapon");
  const [rarity, setRarity] = useState("elite");
  const [rollCls, setRollCls] = useState("Provocator");
  const [preview, setPreview] = useState(null);
  const [rBusy, setRBusy] = useState(false);

  const roll = async () => {
    setRBusy(true);
    try {
      const { data } = await api.post("/admin/items/roll", {
        kind, rarity, cls: kind === "weapon" ? rollCls : null,
      });
      setPreview(data.item);
    } catch (e) { toast.error(apiErr(e)); } finally { setRBusy(false); }
  };
  const keep = async () => {
    setRBusy(true);
    try {
      const { data } = await api.post("/admin/items/roll/keep");
      toast.success(`Kept ${data.item.name} — it's in your armory. List it on the Auction House to sell.`);
      setPreview(null);
    } catch (e) { toast.error(apiErr(e)); } finally { setRBusy(false); }
  };
  const discard = async () => {
    try { await api.post("/admin/items/roll/discard"); } catch { /* ignore */ }
    setPreview(null);
  };

  return (
    <div data-testid="workshop-panel" className="mb-6 border border-amber-700/40 bg-gradient-to-br from-amber-950/20 to-zinc-950 p-4 rounded-sm">
      <div className="flex items-center gap-2 mb-3">
        <Hammer size={16} className="text-amber-400" />
        <h3 className="font-cinzel text-amber-300 uppercase tracking-widest text-sm">Admin Workshop — Create & Roll to Sell</h3>
      </div>
      <p className="text-sm text-zinc-500 mb-4">Create custom gladiators or roll uncommon/rare/elite/legendary gear into your own roster/armory, then list them on the Auction House to sell to players.</p>

      <div className="grid md:grid-cols-2 gap-5">
        {/* Create gladiator */}
        <div className="border border-zinc-800 bg-zinc-950/60 p-4 rounded-sm">
          <div className="flex items-center gap-2 mb-3 text-amber-300"><Swords size={15} /><h4 className="font-cinzel uppercase tracking-widest text-sm">Create Gladiator</h4></div>
          <Label>Name (blank = random)</Label>
          <input data-testid="workshop-glad-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Random name" className={inputCls + " mb-3"} />
          <Label>Class</Label>
          <select data-testid="workshop-glad-class" value={cls} onChange={(e) => setCls(e.target.value)} className={inputCls + " mb-3"}>
            {CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <Label>All skills = </Label>
              <input data-testid="workshop-glad-skill" type="number" min={1} max={1000} value={skillValue} onChange={(e) => setSkillValue(e.target.value)} className={inputCls} />
              <p className="text-[10px] text-zinc-600 mt-1">Every skill ({Object.values(SKILL_LABELS).join(", ")}) is set to this.</p>
            </div>
            <div>
              <Label>Potential grade</Label>
              <select data-testid="workshop-glad-grade" value={grade} onChange={(e) => setGrade(e.target.value)} className={inputCls}>
                {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
          </div>
          <button data-testid="workshop-create-glad-btn" onClick={createGlad} disabled={gBusy}
            className="w-full px-5 py-2 font-cinzel uppercase tracking-wide text-sm rounded-sm bg-amber-500 text-zinc-950 hover:bg-amber-400 disabled:opacity-50">
            {gBusy ? "Creating…" : "Create Gladiator"}
          </button>
        </div>

        {/* Roll gear */}
        <div className="border border-zinc-800 bg-zinc-950/60 p-4 rounded-sm">
          <div className="flex items-center gap-2 mb-3 text-amber-300"><Dices size={15} /><h4 className="font-cinzel uppercase tracking-widest text-sm">Roll Gear</h4></div>
          <div className="flex gap-2 mb-3">
            {[["weapon", "Weapon", Swords], ["armor", "Armor", Shield], ["helmet", "Helmet", HelmetSlotIcon]].map(([v, label, Icon]) => (
              <button key={v} data-testid={`workshop-kind-${v}`} onClick={() => { setKind(v); setPreview(null); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-widest rounded-sm border ${kind === v ? "border-amber-500 bg-amber-900/30 text-amber-200" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
                <Icon size={13} /> {label}
              </button>
            ))}
          </div>
          <div className={`grid ${kind === "weapon" ? "grid-cols-2" : "grid-cols-1"} gap-3 mb-3`}>
            <div>
              <Label>Rarity</Label>
              <select data-testid="workshop-roll-rarity" value={rarity} onChange={(e) => { setRarity(e.target.value); setPreview(null); }} className={inputCls}>
                {ROLL_RARITIES.map((r) => <option key={r} value={r}>{r[0].toUpperCase() + r.slice(1)}</option>)}
              </select>
            </div>
            {kind === "weapon" && (
              <div>
                <Label>Class</Label>
                <select data-testid="workshop-roll-class" value={rollCls} onChange={(e) => { setRollCls(e.target.value); setPreview(null); }} className={inputCls}>
                  {CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            )}
          </div>
          <button data-testid="workshop-roll-btn" onClick={roll} disabled={rBusy}
            className="w-full flex items-center justify-center gap-2 px-5 py-2 font-cinzel uppercase tracking-wide text-sm rounded-sm border border-amber-600 text-amber-300 hover:bg-amber-900/30 disabled:opacity-50">
            <RotateCw size={14} /> {preview ? "Re-roll" : "Roll"}
          </button>

          {preview && (
            <div data-testid="workshop-roll-preview" className="mt-3 border border-zinc-800 bg-zinc-900/50 p-3 rounded-sm">
              <div className="flex items-center justify-between">
                <span className={`font-cinzel ${RARITY_TEXT[preview.rarity] || "text-zinc-200"}`}>{preview.name}</span>
                <span className="text-[10px] uppercase tracking-widest text-zinc-500">{preview.slot}{preview.cls ? ` · ${preview.cls}` : " · any class"}</span>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {Object.entries(preview.base_mods || {}).map(([k, v]) => (
                  <span key={k} className="text-[10px] text-emerald-400">+{v} {SKILL_LABELS[k] || k}</span>
                ))}
              </div>
              {(preview.affixes || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {preview.affixes.map((af, i) => (
                    <span key={i} className="text-[10px] text-violet-300 border border-violet-800/40 bg-violet-950/20 px-1.5 py-0.5 rounded-sm flex items-center gap-1">
                      <Sparkles size={9} /> {af.display}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2 mt-3">
                <button data-testid="workshop-roll-keep" onClick={keep} disabled={rBusy}
                  className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-cinzel uppercase tracking-wide bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm disabled:opacity-50">
                  <Check size={14} /> Keep
                </button>
                <button data-testid="workshop-roll-discard" onClick={discard} disabled={rBusy}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm border border-zinc-700 text-zinc-400 hover:border-red-600 hover:text-red-300 rounded-sm">
                  <Trash2 size={14} /> Discard
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
